import { useCallback, useEffect, useMemo, useState } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import ProductImage from "@/components/ProductImage";
import { normalizeProductList } from "@/lib/apiHelpers";
import { cacheGet, cacheKey, fetchCached } from "@/lib/dataCache";
import { prefetchProductImages } from "@/lib/images";
import ProductCard from "@/components/ProductCard";
import { SlidersHorizontal, X } from "lucide-react";
import { useScrollLock } from "@/lib/scrollLock";
import SeoHead from "@/components/SeoHead";
import { categorySeo } from "@/lib/seoConfig";
import { breadcrumbSchema, categoryCollectionSchema, globalSchemaGraph } from "@/lib/seoSchema";

const SORTS = [
  { v: "newest", l: "Newest" },
  { v: "price_asc", l: "Price: Low → High" },
  { v: "price_desc", l: "Price: High → Low" },
];

const Category = () => {
  const { slug } = useParams();
  const [searchParams] = useSearchParams();
  const isSearch = !slug;
  const q = searchParams.get("q") || "";

  const [sort, setSort] = useState("newest");
  const listKey = useMemo(
    () => cacheKey(["products", { slug: slug || null, q, sort }]),
    [slug, q, sort]
  );

  const [products, setProducts] = useState([]);
  const [loadedKey, setLoadedKey] = useState(null);
  const [fetchFailed, setFetchFailed] = useState(false);
  const [retryCount, setRetryCount] = useState(0);
  const [category, setCategory] = useState(null);
  const [priceCap, setPriceCap] = useState(20000);
  const [activeColors, setActiveColors] = useState([]);
  const [showFilters, setShowFilters] = useState(false);
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== "undefined" ? window.matchMedia("(max-width: 1023px)").matches : false
  );

  useEffect(() => {
    const mq = window.matchMedia("(max-width: 1023px)");
    const onChange = () => setIsMobile(mq.matches);
    onChange();
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);

  const cachedProducts = cacheGet(listKey);
  const listProducts = loadedKey === listKey ? products : cachedProducts;
  const listProductsSafe = useMemo(() => listProducts || [], [listProducts]);
  const showSkeleton = !listProducts;

  const fetchProducts = useCallback(
    () =>
      api.get("/products", {
        params: {
          ...(slug ? { category: slug } : {}),
          ...(q ? { q } : {}),
          sort,
        },
      }).then((r) => normalizeProductList(r.data)),
    [slug, q, sort]
  );

  useEffect(() => {
    const cached = cacheGet(listKey);
    setFetchFailed(false);

    if (cached) {
      setProducts(cached);
      setLoadedKey(listKey);
    } else {
      setProducts([]);
      setLoadedKey(null);
    }

    let active = true;
    const apply = (data) => {
      if (!active) return;
      setProducts(data);
      setLoadedKey(listKey);
      setFetchFailed(false);
    };

    fetchCached(listKey, fetchProducts, { force: retryCount > 0, onUpdate: apply })
      .then(apply)
      .catch(() => {
        if (active && !cached) {
          setFetchFailed(true);
          setLoadedKey(null);
        }
      });

    return () => {
      active = false;
    };
  }, [listKey, fetchProducts, retryCount]);

  useEffect(() => {
    if (!slug) {
      setCategory(null);
      return;
    }

    const categoriesKey = cacheKey(["categories"]);
    const cachedList = cacheGet(categoriesKey);
    if (cachedList) {
      setCategory(cachedList.find((c) => c.slug === slug) || null);
    }

    let active = true;
    const applyCategory = (list) => {
      if (!active) return;
      setCategory(list.find((c) => c.slug === slug) || null);
    };

    fetchCached(categoriesKey, () =>
      api.get("/categories").then((r) => (Array.isArray(r.data) ? r.data : [])),
      { onUpdate: applyCategory }
    )
      .then(applyCategory)
      .catch(() => {
        if (active && !cachedList) setCategory(null);
      });

    return () => {
      active = false;
    };
  }, [slug]);

  useEffect(() => {
    setActiveColors([]);
    setPriceCap(20000);
  }, [slug, q]);

  const colors = useMemo(
    () => [...new Set(listProductsSafe.map((p) => p.color).filter(Boolean))],
    [listProductsSafe]
  );

  const visible = useMemo(() => {
    return listProductsSafe
      .filter((p) => p.price <= priceCap)
      .filter((p) => (activeColors.length ? activeColors.includes(p.color) : true));
  }, [listProductsSafe, priceCap, activeColors]);

  useEffect(() => {
    if (listProductsSafe.length) prefetchProductImages(listProductsSafe, { limit: 20 });
  }, [listProductsSafe]);

  const categoryDisplay = useMemo(() => {
    if (!slug) return null;
    if (category?.slug === slug) return category;
    const list = cacheGet(cacheKey(["categories"]));
    return list?.find((c) => c.slug === slug) || category;
  }, [slug, category]);

  const toggleColor = (c) =>
    setActiveColors((prev) => (prev.includes(c) ? prev.filter((x) => x !== c) : [...prev, c]));

  useScrollLock(showFilters && isMobile);

  const categorySeoMeta = useMemo(() => {
    if (isSearch || !categoryDisplay?.slug) return null;
    return categorySeo(categoryDisplay, listProductsSafe.length);
  }, [isSearch, categoryDisplay, listProductsSafe.length]);

  const categoryJsonLd = useMemo(() => {
    if (isSearch || !categoryDisplay?.slug) return globalSchemaGraph();
    return [
      ...globalSchemaGraph(),
      breadcrumbSchema([
        { name: "Home", path: "/" },
        { name: categoryDisplay.name, path: `/category/${categoryDisplay.slug}` },
      ]),
      categoryCollectionSchema(categoryDisplay, listProductsSafe),
    ];
  }, [isSearch, categoryDisplay, listProductsSafe]);

  const filtersPanel = (
    <aside className="space-y-8" data-testid="filters-panel">
      <div className="flex items-center justify-between">
        <p className="overline">Filters</p>
        <button type="button" onClick={() => setShowFilters(false)} className="min-h-11 min-w-11 inline-flex items-center justify-center lg:hidden" aria-label="Close filters">
          <X className="w-4 h-4" />
        </button>
      </div>
      <div>
        <p className="overline mb-4">Price</p>
        <input
          type="range"
          min={1000}
          max={20000}
          step={500}
          value={priceCap}
          onChange={(e) => setPriceCap(Number(e.target.value))}
          className="w-full accent-primary min-h-11"
          data-testid="price-range"
        />
        <p className="text-xs text-secondary mt-2">Up to ₹{priceCap.toLocaleString("en-IN")}</p>
      </div>
      <div>
        <p className="overline mb-4">Colour</p>
        <div className="flex flex-wrap gap-2">
          {colors.map((c) => (
            <button
              key={c}
              onClick={() => toggleColor(c)}
              data-testid={`color-${c.toLowerCase()}`}
              className={`text-xs px-3 py-2.5 min-h-11 border transition ${
                activeColors.includes(c)
                  ? "border-primary bg-primary text-primary-foreground"
                  : "border-border hover:border-primary"
              }`}
              aria-pressed={activeColors.includes(c)}
            >
              {c}
            </button>
          ))}
        </div>
      </div>
      <button
        onClick={() => { setPriceCap(20000); setActiveColors([]); }}
        className="text-xs uppercase tracking-[0.22em] underline underline-offset-4 min-h-11"
        data-testid="reset-filters"
      >
        Reset filters
      </button>
      <button
        type="button"
        onClick={() => setShowFilters(false)}
        className="btn-primary w-full lg:hidden"
      >
        Show {visible.length} pieces
      </button>
    </aside>
  );

  return (
    <div data-testid="category-page">
      {categorySeoMeta ? (
        <SeoHead {...categorySeoMeta} jsonLd={categoryJsonLd} />
      ) : null}
      <section className="relative">
        {categoryDisplay?.cover_image && (
          <div className="absolute inset-0 -z-10 opacity-[0.18]">
            <ProductImage src={categoryDisplay.cover_image} alt="" sizes="100vw" className="w-full h-full object-cover" />
          </div>
        )}
        <div className="max-w-[1480px] mx-auto px-4 sm:px-8 lg:px-12 pt-16 lg:pt-24 pb-12 text-center">
          <p className="overline mb-4">{isSearch ? "Search results" : "Edit"}</p>
          <h1 className="editorial-h1 break-words">
            {isSearch ? (
              <>Results for <span className="italic">&ldquo;{q}&rdquo;</span></>
            ) : (
              <>
                {categoryDisplay?.name || "Collection"}
                {categoryDisplay?.tagline && (
                  <span className="block text-base sm:text-lg mt-4 not-italic font-sans tracking-normal text-secondary serif italic">
                    {categoryDisplay.tagline}
                  </span>
                )}
              </>
            )}
          </h1>
        </div>
      </section>

      <div className="max-w-[1480px] mx-auto px-4 sm:px-8 lg:px-12 border-y border-border py-3 sm:py-4 flex items-center justify-between gap-2">
        <button
          onClick={() => setShowFilters((v) => !v)}
          className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] min-h-11 px-1"
          data-testid="toggle-filters"
        >
          <SlidersHorizontal className="w-4 h-4" />
          {showFilters ? "Hide" : "Filter"}
        </button>
        <p className="text-xs text-secondary" data-testid="result-count">
          {showSkeleton ? "…" : `${visible.length} pieces`}
        </p>
        <div className="flex items-center gap-2">
          <span className="hidden sm:inline text-xs uppercase tracking-[0.22em] text-secondary">Sort</span>
          <select
            data-testid="sort-select"
            value={sort}
            onChange={(e) => setSort(e.target.value)}
            className="bg-transparent text-xs uppercase tracking-[0.22em] border-0 focus:outline-none cursor-pointer min-h-11 px-2 py-2 max-w-[11rem] sm:max-w-none"
          >
            {SORTS.map((s) => (
              <option key={s.v} value={s.v}>{s.l}</option>
            ))}
          </select>
        </div>
      </div>

      {showFilters && (
        <div className="lg:hidden fixed inset-0 z-50">
          <button type="button" className="absolute inset-0 bg-black/40" aria-label="Close filters" onClick={() => setShowFilters(false)} />
          <div className="absolute bottom-0 left-0 right-0 max-h-[85vh] overflow-y-auto bg-background p-6 rounded-t-lg shadow-xl">
            {filtersPanel}
          </div>
        </div>
      )}

      <div className="max-w-[1480px] mx-auto px-4 sm:px-8 lg:px-12 py-8 sm:py-12 grid lg:grid-cols-12 gap-8">
        {showFilters && (
          <div className="hidden lg:block lg:col-span-3">
            {filtersPanel}
          </div>
        )}

        <div
          className={`${showFilters ? "lg:col-span-9" : "lg:col-span-12"} grid grid-cols-2 ${showFilters ? "lg:grid-cols-3" : "lg:grid-cols-4"} gap-3 sm:gap-4 lg:gap-6`}
        >
          {showSkeleton ? (
            Array.from({ length: 8 }).map((_, i) => (
              <div key={i} className="aspect-[3/4] bg-muted animate-pulse" />
            ))
          ) : fetchFailed ? (
            <div className="col-span-full text-center py-20" data-testid="fetch-error-state">
              <p className="serif text-3xl">Couldn&apos;t load products</p>
              <p className="text-sm text-secondary mt-3">Check your connection and try again.</p>
              <button type="button" onClick={() => setRetryCount((n) => n + 1)} className="btn-primary mt-8 inline-flex">
                Retry
              </button>
            </div>
          ) : visible.length === 0 ? (
            <div className="col-span-full text-center py-20" data-testid="empty-state">
              <p className="serif text-3xl">Nothing matches just yet.</p>
              <p className="text-sm text-secondary mt-3">Try adjusting your filters.</p>
              <Link to="/" className="btn-outline mt-8 inline-flex">Back home</Link>
            </div>
          ) : (
            visible.map((p, i) => <ProductCard key={p.id} product={p} index={i} />)
          )}
        </div>
      </div>
    </div>
  );
};

export default Category;
